import { useRef, useState } from 'react'
import {
  Check,
  ChevronLeft,
  ChevronRight,
  ImagePlus,
  Loader2,
  Pencil,
  Star,
  Trash2,
} from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

import { assetUrl } from '@/lib/api'
import { cn } from '@/lib/utils'
import { downscaleImage } from '@/lib/image'
import { mapWithConcurrency } from '@/lib/concurrency'
import { useOnline } from '@/hooks/useOnline'
import { UPLOAD_CONCURRENCY } from '@/features/inventory/api'
import { BOAT_IMAGE_CATEGORIES, type BoatImage } from '@/types/inventory'
import {
  useBoatImages,
  useUploadBoatImage,
  useUpdateBoatImage,
  useDeleteBoatImage,
} from '@/features/inventory/hooks'

const categoryLabel = (category: string) =>
  BOAT_IMAGE_CATEGORIES.find((c) => c.value === category)?.label ?? category

interface BoatImageGalleryProps {
  boatId: string
}

export function BoatImageGallery({ boatId }: BoatImageGalleryProps) {
  const { data: images = [], isLoading } = useBoatImages(boatId)
  const uploadMutation = useUploadBoatImage(boatId)
  const updateMutation = useUpdateBoatImage(boatId)
  const deleteMutation = useDeleteBoatImage(boatId)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const online = useOnline()

  const [editing, setEditing] = useState<BoatImage | null>(null)
  const [editTitle, setEditTitle] = useState('')
  const [editCategory, setEditCategory] = useState<string>('none')

  // Which photo the main pane shows. Null (or a deleted id) falls back to the
  // cover; the lightbox shares this state so next/prev moves the pane too.
  const [deleting, setDeleting] = useState<BoatImage | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [viewerOpen, setViewerOpen] = useState(false)

  const cover = images.find((img) => img.is_cover)
  const featured = images.find((img) => img.id === selectedId) ?? cover
  const featuredIndex = featured ? images.findIndex((img) => img.id === featured.id) : -1

  const stepViewer = (delta: number) => {
    if (featuredIndex < 0 || images.length < 2) return
    const next = (featuredIndex + delta + images.length) % images.length
    setSelectedId(images[next].id)
  }

  const handleFiles = async (files: FileList | null) => {
    if (!files?.length) return
    const picked = Array.from(files)
    // Explicit sort orders keep the picked order stable under parallel
    // uploads; existing images already occupy 1..images.length.
    const base = images.length
    await mapWithConcurrency(UPLOAD_CONCURRENCY, picked, async (file, i) => {
      // Downscale + strip EXIF client-side; the server re-validates content.
      const processed = await downscaleImage(file)
      await uploadMutation.mutateAsync({ file: processed, sortOrder: base + i + 1 })
    })
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const openEdit = (img: BoatImage) => {
    setEditing(img)
    setEditTitle(img.title)
    setEditCategory(img.category || 'none')
  }

  const saveEdit = () => {
    if (!editing) return
    updateMutation.mutate(
      {
        imageId: editing.id,
        body: {
          title: editTitle,
          category: editCategory === 'none' ? '' : editCategory,
        },
      },
      { onSuccess: () => setEditing(null) },
    )
  }

  const busy = uploadMutation.isPending || deleteMutation.isPending || updateMutation.isPending

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle>Photos</CardTitle>
          <CardDescription>
            {images.length > 0
              ? 'Star a photo to make it the cover shown in the inventory list.'
              : 'Add photos to showcase this boat.'}
          </CardDescription>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          className="hidden"
          onChange={(e) => void handleFiles(e.target.files)}
        />
        {/* Photo bytes can't queue in the offline outbox (FormData payloads
            aren't persistable) — disable instead of failing mid-upload. */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              {/* span so the tooltip works on a disabled button */}
              <span className="inline-flex">
                <Button
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadMutation.isPending || !online}
                >
                  {uploadMutation.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <ImagePlus className="mr-2 h-4 w-4" />
                  )}
                  Upload
                </Button>
              </span>
            </TooltipTrigger>
            {!online && <TooltipContent>Photo uploads need a connection.</TooltipContent>}
          </Tooltip>
        </TooltipProvider>
      </CardHeader>
      <CardContent>
        {uploadMutation.isError && (
          <p className="mb-4 text-sm text-destructive">
            Upload failed: {uploadMutation.error.message}
          </p>
        )}

        {isLoading ? (
          <div className="flex flex-col gap-3 md:flex-row">
            <Skeleton className="h-80 flex-1" />
            <div className="grid grid-cols-4 gap-2 sm:grid-cols-6 md:w-56 md:grid-cols-2">
              <Skeleton className="aspect-square w-full" />
              <Skeleton className="aspect-square w-full" />
              <Skeleton className="aspect-square w-full" />
              <Skeleton className="aspect-square w-full" />
            </div>
          </div>
        ) : images.length === 0 ? (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="flex h-80 w-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed text-muted-foreground transition-colors hover:border-primary hover:text-primary"
          >
            <ImagePlus className="h-8 w-8" />
            <span className="text-sm">Click to add the first photo</span>
          </button>
        ) : (
          <div className="flex flex-col gap-3 md:flex-row md:items-start">
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              {featured && (
                <>
                  <button
                    type="button"
                    className="relative flex h-80 w-full cursor-zoom-in items-center justify-center overflow-hidden rounded-lg border bg-muted"
                    onClick={() => setViewerOpen(true)}
                    title="View full size"
                  >
                    <img
                      src={assetUrl(featured.url)}
                      alt={featured.title || featured.id}
                      className="max-h-full max-w-full object-contain"
                      loading="eager"
                    />
                    {featured.is_cover && <Badge className="absolute left-3 top-3">Cover</Badge>}
                    {featured.category && (
                      <Badge
                        variant="outline"
                        className="absolute bottom-3 right-3 bg-background/80"
                      >
                        {categoryLabel(featured.category)}
                      </Badge>
                    )}
                  </button>
                  {featured.title && (
                    <p className="text-sm font-medium text-muted-foreground">{featured.title}</p>
                  )}
                </>
              )}
            </div>

            {/* p-0.5 keeps the selected tile's 2px ring inside the scrollport:
                md:overflow-y-auto also clips horizontal ink overflow, which
                would otherwise shear the ring off on the outer edges. */}
            <div className="grid shrink-0 grid-cols-4 gap-2 p-0.5 sm:grid-cols-6 md:max-h-80 md:w-56 md:grid-cols-2 md:overflow-y-auto">
              {images.map((img) => (
                <div
                  key={img.id}
                  className={cn(
                    'group relative overflow-hidden rounded-md border transition-opacity',
                    img.id === featured?.id
                      ? 'border-white ring-2 ring-white'
                      : 'border-border opacity-70 hover:opacity-100',
                  )}
                >
                  {/* The aspect ratio must live on this in-flow <img>, not on
                      the wrapper: WebKit (Safari) ignores an aspect-ratio'd
                      grid item's height for row sizing when the item has
                      overflow-hidden and only absolutely-positioned children,
                      so every row collapses and the thumbnails stack on top of
                      each other. */}
                  <img
                    src={assetUrl(img.thumb_url)}
                    alt={img.title || img.id}
                    className="aspect-square w-full object-cover"
                    loading="lazy"
                  />
                  <button
                    type="button"
                    className="absolute inset-0 cursor-pointer"
                    title={img.title || undefined}
                    onClick={() => setSelectedId(img.id)}
                    aria-label="Show this photo in the main viewer"
                    aria-pressed={img.id === featured?.id}
                  />
                  {img.id === featured?.id && (
                    <span
                      aria-hidden
                      className="pointer-events-none absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-white text-background shadow"
                    >
                      <Check className="h-3 w-3" strokeWidth={3} />
                    </span>
                  )}
                  {img.is_cover && (
                    <Badge
                      variant="secondary"
                      className="pointer-events-none absolute left-1.5 top-1.5"
                    >
                      <Star className="mr-1 h-3 w-3" /> Cover
                    </Badge>
                  )}
                  {img.category && (
                    <Badge
                      variant="secondary"
                      className="pointer-events-none absolute bottom-1.5 right-1.5 px-1.5 text-[10px]"
                    >
                      {categoryLabel(img.category)}
                    </Badge>
                  )}
                  {/*
                    The whole tile selects the photo, so the actions live in a
                    bottom strip instead of a full-size overlay — a centered
                    overlay leaves nowhere clickable on tiles this small. The
                    strip stays visible below md (hover-capable) widths so
                    touch devices can reach it.
                  */}
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center gap-1 bg-black/50 p-1 transition-opacity max-md:opacity-100 md:opacity-0 md:group-hover:opacity-100">
                    {!img.is_cover && (
                      <Button
                        type="button"
                        variant="secondary"
                        size="icon"
                        className="pointer-events-auto h-7 w-7 rounded-sm"
                        title="Set as cover"
                        disabled={busy}
                        onClick={() =>
                          updateMutation.mutate({ imageId: img.id, body: { is_cover: true } })
                        }
                      >
                        <Star className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="secondary"
                      size="icon"
                      className="pointer-events-auto h-7 w-7 rounded-sm"
                      title="Edit title and category"
                      disabled={busy}
                      onClick={() => openEdit(img)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="pointer-events-auto h-7 w-7 rounded-sm"
                      title="Delete photo"
                      disabled={busy}
                      onClick={() => setDeleting(img)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>

      <Dialog open={editing !== null} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Edit photo details</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="photo-title">Title</Label>
              <Input
                id="photo-title"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                placeholder="e.g. Starboard side"
              />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={editCategory} onValueChange={setEditCategory}>
                <SelectTrigger>
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {BOAT_IMAGE_CATEGORIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {updateMutation.isError && (
              <p className="text-sm text-destructive">
                Update failed: {updateMutation.error.message}
              </p>
            )}
            <Button className="w-full" disabled={updateMutation.isPending} onClick={saveEdit}>
              {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={viewerOpen && featured != null} onOpenChange={setViewerOpen}>
        <DialogContent
          className="border-none bg-black/95 p-2 sm:max-w-5xl"
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft') stepViewer(-1)
            else if (e.key === 'ArrowRight') stepViewer(1)
          }}
        >
          <DialogTitle className="sr-only">{featured?.title || 'Boat photo'}</DialogTitle>
          <div className="relative flex items-center justify-center">
            {featured && (
              <img
                src={assetUrl(featured.url)}
                alt={featured.title || featured.id}
                className="max-h-[80vh] w-auto max-w-full rounded-md object-contain"
              />
            )}
            {images.length > 1 && (
              <>
                <Button
                  type="button"
                  variant="secondary"
                  size="icon"
                  className="absolute left-2 top-1/2 h-9 w-9 -translate-y-1/2 rounded-full"
                  title="Previous photo"
                  onClick={() => stepViewer(-1)}
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="icon"
                  className="absolute right-2 top-1/2 h-9 w-9 -translate-y-1/2 rounded-full"
                  title="Next photo"
                  onClick={() => stepViewer(1)}
                >
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </>
            )}
          </div>
          <div className="flex items-center justify-between px-2 pb-1 text-sm text-white/70">
            <span className="truncate">
              {featured?.title || 'Untitled'}
              {featured?.category ? ` · ${categoryLabel(featured.category)}` : ''}
            </span>
            {images.length > 1 && (
              <span className="ml-3 shrink-0">
                {featuredIndex + 1} of {images.length}
              </span>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo delete confirmation. */}
      <Dialog open={deleting !== null} onOpenChange={(open) => !open && setDeleting(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete this photo?</DialogTitle>
            <DialogDescription>
              “{deleting?.title || 'Untitled'}” is permanently removed from the gallery.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={deleteMutation.isPending}
              onClick={() =>
                deleting &&
                deleteMutation.mutate(deleting.id, { onSuccess: () => setDeleting(null) })
              }
            >
              Delete Photo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}
